// Draft-only, shopper-initiated estimates. No address persistence or cart writes.
// https://shopify.dev/docs/api/ajax/reference/cart#generate-shipping-rates
(() => {
  if (customElements.get('st-retail-shipping')) return;

  const MAX_POLLS = 6;
  const POLL_DELAY_MS = 1000;
  const REQUEST_TIMEOUT_MS = 20000;
  const STATES = new Set([
    'Australian Capital Territory', 'New South Wales', 'Northern Territory',
    'Queensland', 'South Australia', 'Tasmania', 'Victoria', 'Western Australia',
  ]);
  const CART_EVENTS = ['cartChange', 'cart:change', 'cart:updated', 'cart-update', 'soundtrap:cart-updated', 'soundtrap:cart-drawer-rendered'];
  let nextId = 0;

  customElements.define('st-retail-shipping', class extends HTMLElement {
    connectedCallback() {
      if (this.listeners) return;
      this.postcode = this.querySelector('[data-shipping-postcode]');
      this.province = this.querySelector('[data-shipping-province]');
      this.button = this.querySelector('[data-shipping-estimate]');
      this.status = this.querySelector('[data-shipping-status]');
      this.rates = this.querySelector('[data-shipping-rates]');
      if (!this.postcode || !this.province || !this.button || !this.status || !this.rates) return;

      this.version = (this.version || 0) + 1;
      this.knownEmpty = this.dataset.itemCount === '0';
      this.listeners = new AbortController();
      const options = { signal: this.listeners.signal };
      this.status.id ||= `STShippingStatus-${++nextId}`;
      this.postcode.setAttribute('aria-describedby', this.status.id);
      this.province.setAttribute('aria-describedby', this.status.id);
      this.button.addEventListener('click', () => this.estimate(), options);
      const onAddressChange = event => {
        if (event.target === this.postcode || event.target === this.province) {
          // Dawn treats every bubbling cart change as a quantity change.
          event.stopPropagation();
          this.invalidate();
        }
      };
      this.addEventListener('input', onAddressChange, options);
      this.addEventListener('change', onAddressChange, options);
      this.addEventListener('keydown', event => {
        if (event.key === 'Enter' && (event.target === this.postcode || event.target === this.province)) {
          event.preventDefault();
          event.stopPropagation();
          this.estimate();
        }
      }, options);

      const onCartChange = event => {
        const cart = event?.detail?.cartData || event?.cartData;
        this.knownEmpty = cart?.item_count === 0;
        this.invalidate(this.knownEmpty ? 'Add an item to estimate shipping.' : 'Cart changed. Estimate again.');
      };
      CART_EVENTS.forEach(name => document.addEventListener(name, onCartChange, options));
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribeCart = subscribe(PUB_SUB_EVENTS.cartUpdate, onCartChange);
      }
      // Invalidate before an asynchronous quantity/remove mutation finishes.
      document.addEventListener('input', event => {
        if (event.target.matches?.('cart-items [name="updates[]"], cart-drawer-items [name="updates[]"]')) onCartChange();
      }, options);
      document.addEventListener('click', event => {
        if (event.target.closest?.('cart-remove-button')) onCartChange();
      }, options);
      document.addEventListener('st-retail-shipping:request', event => {
        if (event.detail?.source !== this) this.invalidate();
      }, options);
      window.addEventListener('pageshow', event => {
        if (event.persisted) onCartChange();
      }, options);
      this.invalidate();
    }

    disconnectedCallback() {
      this.invalidate();
      this.listeners?.abort();
      this.listeners = null;
      this.unsubscribeCart?.();
      this.unsubscribeCart = null;
    }

    invalidate(message = '') {
      this.version = (this.version || 0) + 1;
      this.controller?.abort();
      this.controller = null;
      clearTimeout(this.deadline);
      this.busy = false;
      this.removeAttribute('aria-busy');
      if (this.button) this.button.disabled = this.knownEmpty;
      if (this.status) this.status.textContent = message;
      if (this.rates) {
        this.rates.replaceChildren();
        this.rates.hidden = true;
      }
      this.postcode?.removeAttribute('aria-invalid');
      this.province?.removeAttribute('aria-invalid');
    }

    isCurrent(version, controller) {
      return this.isConnected && version === this.version && !controller.signal.aborted;
    }

    async read(path, method, controller) {
      const root = this.dataset.rootUrl || window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root.endsWith('/') ? root : root + '/'}${path}`, {
        method, credentials: 'same-origin', cache: 'no-store',
        headers: { Accept: 'application/json' }, signal: controller.signal,
      });
      if (!response.ok) throw new Error(response.status === 429 ? 'busy' : 'request');
      // Preparing rates only needs a successful status; Shopify may send no body.
      if (method === 'POST') return null;
      return response.json();
    }

    cartSnapshot(cart) {
      if (!cart || !Array.isArray(cart.items) || !Number.isInteger(cart.item_count) || cart.item_count < 0) throw new Error('cart');
      return JSON.stringify({
        item_count: cart.item_count, items: cart.items, currency: cart.currency, total_price: cart.total_price,
        total_weight: cart.total_weight, discounts: cart.cart_level_discount_applications,
      });
    }

    delay(signal) {
      return new Promise((resolve, reject) => {
        const abort = () => {
          clearTimeout(timer);
          reject(new DOMException('Request cancelled', 'AbortError'));
        };
        const timer = setTimeout(() => {
          signal.removeEventListener('abort', abort);
          resolve();
        }, POLL_DELAY_MS);
        if (signal.aborted) abort();
        else signal.addEventListener('abort', abort, { once: true });
      });
    }

    async estimate() {
      if (!this.isConnected || !this.button || this.busy) return;
      this.invalidate();
      if (this.knownEmpty) {
        this.status.textContent = 'Add an item to estimate shipping.';
        return;
      }
      const zip = this.postcode.value.trim();
      const province = this.province.value;
      if (!/^\d{4}$/.test(zip) || !STATES.has(province)) {
        const invalid = !/^\d{4}$/.test(zip) ? this.postcode : this.province;
        invalid.setAttribute('aria-invalid', 'true');
        this.status.textContent = invalid === this.postcode ? 'Enter a four-digit Australian postcode.' : 'Select a state or territory.';
        invalid.focus();
        return;
      }

      // Only one on-page instance may prepare rates for this cart at a time.
      document.dispatchEvent(new CustomEvent('st-retail-shipping:request', { detail: { source: this } }));
      const controller = new AbortController();
      this.controller = controller;
      const version = this.version;
      this.busy = true;
      this.button.disabled = true;
      this.setAttribute('aria-busy', 'true');
      this.status.textContent = 'Checking shipping costs...';
      this.deadline = setTimeout(() => {
        if (this.isCurrent(version, controller)) this.invalidate('Shipping estimates are taking longer than expected. Try again or check at checkout.');
      }, REQUEST_TIMEOUT_MS);

      try {
        const cart = await this.read('cart.js', 'GET', controller);
        if (!this.isCurrent(version, controller)) return;
        const snapshot = this.cartSnapshot(cart);
        if (cart.item_count === 0 || cart.items.length === 0) {
          this.knownEmpty = true;
          this.status.textContent = 'Add an item to estimate shipping.';
          return;
        }
        const query = new URLSearchParams({
          'shipping_address[zip]': zip,
          'shipping_address[country]': 'Australia',
          'shipping_address[province]': province,
        }).toString();
        await this.read(`cart/prepare_shipping_rates.json?${query}`, 'POST', controller);
        if (!this.isCurrent(version, controller)) return;

        for (let attempt = 0; attempt < MAX_POLLS; attempt += 1) {
          const result = await this.read(`cart/async_shipping_rates.json?${query}`, 'GET', controller);
          if (!this.isCurrent(version, controller)) return;
          if (result !== null) {
            if (!result || !Array.isArray(result.shipping_rates)) throw new Error('rates');
            const freshCart = await this.read('cart.js', 'GET', controller);
            if (!this.isCurrent(version, controller)) return;
            if (this.cartSnapshot(freshCart) !== snapshot) {
              this.knownEmpty = freshCart.item_count === 0;
              this.status.textContent = this.knownEmpty ? 'Add an item to estimate shipping.' : 'Cart changed. Estimate again.';
              return;
            }
            this.renderRates(result.shipping_rates, cart.currency, zip, province);
            return;
          }
          if (attempt + 1 < MAX_POLLS) await this.delay(controller.signal);
          if (!this.isCurrent(version, controller)) return;
        }
        this.status.textContent = 'Shipping estimates are taking longer than expected. Try again or check at checkout.';
      } catch (error) {
        if (!this.isCurrent(version, controller)) return;
        this.status.textContent = error.message === 'busy'
          ? 'Shipping estimates are busy. Try again shortly or check at checkout.'
          : 'Could not get a shipping estimate. Check the postcode and state, or check options at checkout.';
      } finally {
        if (this.isCurrent(version, controller)) {
          clearTimeout(this.deadline);
          this.busy = false;
          this.button.disabled = this.knownEmpty;
          this.removeAttribute('aria-busy');
        }
      }
    }

    renderRates(rates, cartCurrency, zip, province) {
      // Prices are decimal major units, not cart cents. Never render date fields.
      const rows = rates.map(rate => {
        if (!rate || !['string', 'number'].includes(typeof rate.price)) throw new Error('price');
        const currency = rate.currency ?? cartCurrency;
        if (typeof currency !== 'string' || !/^[A-Z]{3}$/.test(currency)) throw new Error('currency');
        const rawPrice = String(rate.price ?? '');
        if (!/^\d+(\.\d+)?$/.test(rawPrice) || !Number.isFinite(Number(rawPrice))) throw new Error('price');
        const row = document.createElement('li');
        row.className = 'st-retail-shipping__rate';
        const name = document.createElement('span');
        name.textContent = rate.presentment_name || rate.name || 'Shipping';
        const price = document.createElement('span');
        price.className = 'st-retail-shipping__price';
        price.textContent = new Intl.NumberFormat('en-AU', { style: 'currency', currency, currencyDisplay: 'code' }).format(Number(rawPrice));
        row.append(name, price);
        return row;
      });
      this.rates.replaceChildren(...rows);
      this.rates.hidden = rows.length === 0;
      this.status.textContent = rows.length
        ? `Estimated shipping to ${zip}, ${province}:`
        : 'No shipping estimate was returned for this address. Check available options at checkout.';
    }
  });
})();
